/* eslint-disable radix */
import { editManualCharge } from 'graphql/ledger/mutations';
import { getLedgerListings } from 'graphql/ledger/queries';
import { useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { listingItemtype } from 'types/ledger';
import useAlert from 'utils/alert';
import { validateAllowedKindTypes } from 'utils/filterItemsbykind';
import { capitalize } from 'utils/helpers';
import { AccountingType } from 'v2-types/order';

import { addManualCharge } from './services/mutations';
import { EntryLedgerType } from './services/types';

const useAddRecipts = (
  handleClose: () => void,
  prevData: AccountingType | null,
  handleSuccess?: () => void,
  kindAllowedEntry?: string | string[],
  codeAllowedEntry?: string | string[],
  lettersAllowedEntry?: string | string[],
  resolver?: Resolver<EntryLedgerType, object> | undefined,
  isEditingEntry?: boolean,
  onAddEntry?: () => void,
  defaultCode?: string,
  defaultKind?: string
) => {
  const { t } = useTranslation();
  const [createErrors, setcreateErrors] = useState<string[]>([]);
  const { id: orderId } = useParams<{ id: string }>();
  const showAlert = useAlert();
  const [allowedCodes, setAllowedCodes] = useState<listingItemtype[]>([]);
  const [allowedKinds, setAllowedKinds] = useState<listingItemtype[]>([]);
  const [allowedLetters, setAllowedLetters] = useState<listingItemtype[]>([]);
  const methods = useForm<EntryLedgerType>({
    defaultValues: {
      entryDate: prevData?.entry_date || null,
      description: prevData?.description || '',
      code: prevData?.code != null ? prevData?.code : (defaultCode ?? ''),
      amount: prevData?.amount || 0,
      kind: prevData?.kind != null ? prevData?.kind : (defaultKind ?? ''),
      letter: prevData?.letter || '',
      number: prevData?.number || null,
      amount_override: prevData?.amount_override || prevData?.amount,
      amount_calculated: prevData?.amount_calculated || 0,
      months: prevData?.months || 12,
      months_advanced: prevData?.months_advanced || null,
      annuality: prevData?.annuality || null,
    },
    resolver,
  });

  const { mutate: AddManualcharge, ...responseAddManualcharge } = useMutation(addManualCharge, {
    onSuccess: (data) => {
      if (data) {
        showAlert(capitalize(t('dialogs:order-updated')), 'success');
        if (handleSuccess) handleSuccess();
        responseAddManualcharge.reset();
        setcreateErrors([]);
        methods.reset();
        handleClose();
      }
    },
    onError: (e: any) => {
      const { errors = ['Error Adding Association'] } = e?.response || {};
      const errorsMapped = errors.map((error: any) => error.message);
      setcreateErrors(errorsMapped);
    },
  });

  const { mutate: EditManualcharge, ...responseEditManualcharge } = useMutation(editManualCharge, {
    onSuccess: (data, variables) => {
      if (data) {
        showAlert(capitalize(t('dialogs:order-updated')), 'success');
        responseEditManualcharge.reset();
        setcreateErrors([]);
        methods.reset();

        let editedEntry: AccountingType | null = null;
        if (prevData) {
          editedEntry = {
            ...prevData,
            amount: variables.data.amount_override ?? 0,
          };
        }

        if (handleSuccess) handleSuccess();
        handleClose();
      }
    },
    onError: (e: any) => {
      const { errors = ['Error Adding Association'] } = e?.response || {};
      const errorsMapped = errors.map((error: any) => error.message);
      setcreateErrors(errorsMapped);
    },
  });
  const getLedgerAllowedKinds = useQuery(
    ['allowed-ledger-kinds', 'allowed_ledger_kinds'],
    () => getLedgerListings('allowed_ledger_kinds'),
    {
      enabled: !prevData?.kind,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data.getListing.entries) {
          setAllowedKinds(validateAllowedKindTypes(data?.getListing?.entries, kindAllowedEntry));
        }
      },
    }
  );

  const getLedgerAllowedCodes = useQuery(
    ['allowed-ledger-Codes', 'allowed_ledger_codes'],
    () => getLedgerListings('allowed_ledger_codes'),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data.getListing.entries) {
          setAllowedCodes(validateAllowedKindTypes(data?.getListing?.entries, codeAllowedEntry));
        }
      },
    }
  );

  const getLedgerAllowedLetters = useQuery(
    ['allowed_ledger_letters', 'allowed_ledger_letters'],
    () => getLedgerListings('allowed_ledger_letters'),
    {
      refetchOnWindowFocus: false,
      enabled: !prevData?.letter || (!!lettersAllowedEntry && lettersAllowedEntry.length > 0),
      onSuccess: (data) => {
        if (data.getListing.entries) {
          setAllowedLetters(
            validateAllowedKindTypes(data?.getListing?.entries, lettersAllowedEntry)
          );
        }
      },
    }
  );

  const handleEntry = methods.handleSubmit((data) => {
    if (isEditingEntry) {
      EditManualcharge({
        id: orderId!,
        accountingId: prevData?._id!,
        data: {
          amount_override: parseFloat(data.amount_override as unknown as string) || 0,
          entry_date: data?.entryDate || null,
          letter: data.letter === '' ? null : data.letter,
          months_advanced: data.months_advanced
            ? parseInt(data.months_advanced as unknown as string)
            : null,
          number: data.number ? parseFloat(data.number as unknown as string) : null,
          months: data.months ? parseInt(data.months as unknown as string) : null,
          annuality: parseFloat(data.annuality as unknown as string) || null,
          description: data.description,
        },
      });
      return;
    }

    AddManualcharge({
      id: orderId!,
      description: data.description,
      code: data.code,
      kind: data.kind,
      number: data.number ? parseFloat(data.number as unknown as string) : null,
      amount: parseFloat(data.amount as unknown as string) || 0,
      months: data.months ? parseInt(data.months as unknown as string) : null,
      entryDate: data?.entryDate,
      letter: data.letter === '' ? null : data.letter,
      months_advanced: data.months_advanced
        ? parseInt(data.months_advanced as unknown as string)
        : null,
      annuality: parseFloat(data.annuality as unknown as string) || null,
    });
    if (onAddEntry) {
      onAddEntry();
    }
  });

  return {
    allowedCodes,
    allowedKinds,
    allowedLetters,
    loadingKinds: getLedgerAllowedKinds.isLoading,
    loadingCodes: getLedgerAllowedCodes.isLoading,
    loadingLetters: getLedgerAllowedLetters.isLoading,
    loadingEditEntry: responseEditManualcharge.isLoading,
    loadingAddNewEntry: responseAddManualcharge.isLoading,
    handleEntry,
    methods,
    createErrors,
    t,
  };
};

export default useAddRecipts;
